import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '@/shared/store/hooks';
import {
  isIosSafari,
  isStandalone,
  promptInstall as promptNativeInstall,
  readInstallDismissal,
  rememberInstallDismissal,
  type InstallOutcome,
} from './installPrompt';

export interface InstallPromptState {
  /** El navegador ofrece el diálogo nativo de instalación (Chromium/Android/escritorio). */
  canInstall: boolean;
  /** Safari de iOS: no hay diálogo; se muestran las instrucciones "Compartir → Añadir a inicio". */
  showIosInstructions: boolean;
  /** Mostrar el aviso de instalación: hay forma de instalar, no está instalada y no se descartó hace poco. */
  showBanner: boolean;
  /** Abre el diálogo nativo. */
  promptInstall: () => Promise<InstallOutcome>;
  /** "Ahora no": oculta el aviso durante 14 días. */
  dismiss: () => void;
}

function detectIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isIosSafari(navigator.userAgent, navigator.platform, navigator.maxTouchPoints);
}

/** Estado y acciones para ofrecer la instalación de la PWA. Lee `ui.installAvailable` del store. */
export function useInstallPrompt(): InstallPromptState {
  const canInstall = useAppSelector((state) => state.ui.installAvailable);
  const [dismissed, setDismissed] = useState<boolean>(() => readInstallDismissal());
  const standalone = useMemo(() => isStandalone(), []);
  const showIosInstructions = useMemo(() => !standalone && detectIosSafari(), [standalone]);

  const dismiss = useCallback(() => {
    rememberInstallDismissal();
    setDismissed(true);
  }, []);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    const outcome = await promptNativeInstall();
    if (outcome === 'dismissed') dismiss();
    return outcome;
  }, [dismiss]);

  return {
    canInstall,
    showIosInstructions,
    showBanner: !standalone && !dismissed && (canInstall || showIosInstructions),
    promptInstall,
    dismiss,
  };
}
